import { computed } from 'vue'
import { usePermissionStore } from '~/stores/permissions'
import { useAuthStore } from '~/stores/auth'

export function usePermissions() {
  const permissionStore = usePermissionStore()
  const authStore = useAuthStore()
  
  /**
   * Check if the current user is a superuser
   */
  const isSuperuser = computed(() => {
    return authStore.user?.is_superuser || false
  })
  
  /**
   * Check if the current user has a specific permission
   */
  const hasPermission = (resourceType: string, action: string): boolean => {
    // Superusers have all permissions
    if (isSuperuser.value) {
      return true
    }
    
    return permissionStore.hasPermission(resourceType, action)
  }
  
  /**
   * Check if the current user has any permission for a resource type
   */
  const hasAnyPermissionForResource = (resourceType: string): boolean => {
    // Superusers have all permissions
    if (isSuperuser.value) {
      return true
    }
    
    return permissionStore.hasAnyPermissionForResource(resourceType)
  }
  
  /**
   * Check if the current user is the owner of a resource
   * This is a client-side check and should be used for UI purposes only
   * The server will enforce ownership checks for security
   */
  const isResourceOwner = (resourceType: string, resource: any): boolean => {
    // Superusers are treated as owners of all resources
    if (isSuperuser.value) {
      return true
    }
    
    // If not authenticated or no user, not an owner
    if (!authStore.isAuthenticated || !authStore.user) {
      return false
    }
    
    // Check ownership based on resource type
    switch (resourceType) {
      case 'app':
        return resource.owner_id === authStore.user.id
      // Add more resource types as needed
      default:
        return false
    }
  }
  
  /**
   * Check if the current user can perform an action on a resource
   * Either by having the permission or by being the resource owner
   */
  const canAccessResource = (resourceType: string, action: string, resource?: any): boolean => {
    // Superusers can access all resources
    if (isSuperuser.value) {
      return true
    }
    
    // Check if user has the permission
    const hasRequiredPermission = hasPermission(resourceType, action)
    
    // If no resource provided or user has the permission, return permission check result
    if (!resource || hasRequiredPermission) {
      return hasRequiredPermission
    }
    
    // If user doesn't have the permission, check if they're the resource owner
    // Owners can typically view, edit, and delete their own resources
    if (['read', 'update', 'delete'].includes(action)) {
      return isResourceOwner(resourceType, resource)
    }
    
    return false
  }
  
  return {
    hasPermission,
    hasAnyPermissionForResource,
    isResourceOwner,
    canAccessResource,
    isSuperuser,
    permissions: computed(() => permissionStore.permissions),
    isLoading: computed(() => permissionStore.isLoading),
    isInitialized: computed(() => permissionStore.isInitialized),
  }
}
